import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { format, parseISO, formatDistanceToNowStrict } from 'date-fns';
import { SignalIcon, GlobeAltIcon, DevicePhoneMobileIcon, ComputerDesktopIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface LiveEvent {
  id: string;
  type: 'page_view' | 'new_session';
  path?: string;
  device?: string;
  browser?: string;
  browser_version?: string;
  os?: string;
  country?: string;
  city?: string;
  ip_address?: string;
  ts: string;
  is_returning?: boolean;
}

interface ActivePage {
  path: string;
  count: number;
}

interface Props {
  enabled: boolean;
}

function DeviceIcon({ type }: { type?: string }) {
  if (type === 'mobile') return <DevicePhoneMobileIcon className="w-3.5 h-3.5" />;
  if (type === 'tablet') return <DevicePhoneMobileIcon className="w-3.5 h-3.5" />;
  return <ComputerDesktopIcon className="w-3.5 h-3.5" />;
}

function getBrowserColor(browser?: string) {
  const b = (browser || '').toLowerCase();
  if (b.includes('chrome')) return 'bg-yellow-100 text-yellow-700';
  if (b.includes('firefox')) return 'bg-orange-100 text-orange-700';
  if (b.includes('safari')) return 'bg-blue-100 text-blue-700';
  if (b.includes('edge')) return 'bg-cyan-100 text-cyan-700';
  return 'bg-gray-100 text-gray-600';
}

export default function RealtimeFeed({ enabled }: Props) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [activeNow, setActiveNow] = useState(0);
  const [activePages, setActivePages] = useState<ActivePage[]>([]);
  const [viewsPerMin, setViewsPerMin] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState<'connecting' | 'ok' | 'error'>('connecting');
  const containerRef = useRef<HTMLDivElement>(null);
  const recentViewsRef = useRef<number[]>([]);
  const presenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastPolledAtRef = useRef<string>(new Date().toISOString());

  useEffect(() => {
    if (!enabled) return;

    const presenceChannel = supabase.channel('visitor-presence', {
      config: { presence: { key: `admin-${Math.random().toString(36).slice(2)}` } },
    });
    presenceChannelRef.current = presenceChannel;

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        const visitorKeys = Object.keys(state).filter(k => !k.startsWith('admin-'));
        setActiveNow(visitorKeys.length);

        const pageCounts: Record<string, number> = {};
        Object.entries(state).forEach(([key, presences]: [string, any[]]) => {
          if (key.startsWith('admin-')) return;
          presences.forEach((p: any) => {
            if (p.path) pageCounts[p.path] = (pageCounts[p.path] ?? 0) + 1;
          });
        });
        setActivePages(
          Object.entries(pageCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([path, count]) => ({ path, count }))
        );
      })
      .subscribe(status => {
        if (status === 'SUBSCRIBED') setRealtimeStatus('ok');
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setRealtimeStatus('error');
      });

    function addPageViewEvent(r: any) {
      if (r.user_id) return;
      const now = Date.now();
      recentViewsRef.current = [...recentViewsRef.current.filter(t => now - t < 60000), now];
      setViewsPerMin(recentViewsRef.current.length);
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 600);
      setEvents(prev => {
        if (prev.some(e => e.id === r.id)) return prev;
        return [{
          id: r.id,
          type: 'page_view',
          path: r.path,
          device: r.device_type,
          browser: r.browser,
          browser_version: r.browser_version,
          os: r.os,
          ip_address: r.ip_address,
          ts: r.created_at,
        }, ...prev].slice(0, 60);
      });
    }

    function addSessionEvent(r: any) {
      if (r.user_id) return;
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 600);
      setEvents(prev => {
        if (prev.some(e => e.id === r.id)) return prev;
        return [{
          id: r.id,
          type: 'new_session',
          path: r.entry_path,
          device: r.device_type,
          browser: r.browser,
          browser_version: r.browser_version,
          os: r.os,
          country: r.country,
          city: r.city,
          ip_address: r.ip_address,
          is_returning: r.is_returning,
          ts: r.started_at,
        }, ...prev].slice(0, 60);
      });
    }

    const pvChannel = supabase
      .channel('realtime-pv-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'page_views' },
        payload => addPageViewEvent(payload.new)
      )
      .subscribe(status => {
        if (status === 'SUBSCRIBED') setRealtimeStatus('ok');
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setRealtimeStatus('error');
      });

    const sessionChannel = supabase
      .channel('realtime-session-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'visitor_sessions' },
        payload => addSessionEvent(payload.new)
      )
      .subscribe();

    const pollInterval = setInterval(async () => {
      const since = lastPolledAtRef.current;
      const now = new Date().toISOString();

      const [pvRes, sessRes] = await Promise.all([
        supabase
          .from('page_views')
          .select('id, path, device_type, browser, browser_version, os, ip_address, created_at, user_id')
          .gt('created_at', since)
          .is('user_id', null)
          .order('created_at', { ascending: true }),
        supabase
          .from('visitor_sessions')
          .select('id, entry_path, device_type, browser, browser_version, os, country, city, ip_address, is_returning, started_at, user_id')
          .gt('started_at', since)
          .is('user_id', null)
          .order('started_at', { ascending: true }),
      ]);

      if (!pvRes.error && !sessRes.error) {
        lastPolledAtRef.current = now;
        (pvRes.data ?? []).forEach(r => addPageViewEvent(r));
        (sessRes.data ?? []).forEach(r => addSessionEvent(r));
      }
    }, 5000);

    const vpmTimer = setInterval(() => {
      const now = Date.now();
      recentViewsRef.current = recentViewsRef.current.filter(t => now - t < 60000);
      setViewsPerMin(recentViewsRef.current.length);
    }, 10000);

    return () => {
      supabase.removeChannel(pvChannel);
      supabase.removeChannel(sessionChannel);
      if (presenceChannelRef.current) supabase.removeChannel(presenceChannelRef.current);
      clearInterval(vpmTimer);
      clearInterval(pollInterval);
    };
  }, [enabled]);

  if (!enabled) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col items-center justify-center py-16 text-center">
        <SignalIcon className="w-8 h-8 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 font-medium">Realtime tracking is off</p>
        <p className="text-xs text-gray-400 mt-1">Enable it in Analytics Settings to see live visitor activity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {realtimeStatus === 'error' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-700 font-medium">
          WebSocket stream unavailable — showing live data via polling (5s). To fix, ensure <code className="font-mono">page_views</code> and <code className="font-mono">visitor_sessions</code> are added to the <code className="font-mono">supabase_realtime</code> publication in your Supabase dashboard under Database &rarr; Replication.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center justify-center">
          <div className="flex items-center space-x-1.5 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Now</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{activeNow}</p>
          <p className="text-xs text-gray-400 mt-0.5">via Presence</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center justify-center">
          <div className="flex items-center space-x-1.5 mb-1">
            <ArrowPathIcon className={`w-3 h-3 ${isPulsing ? 'text-golf-orange animate-spin' : 'text-gray-400'}`} />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Views / min</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{viewsPerMin}</p>
          <p className="text-xs text-gray-400 mt-0.5">last 60 seconds</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center justify-center">
          <div className="flex items-center space-x-1.5 mb-1">
            <GlobeAltIcon className="w-3 h-3 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Live Events</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{events.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">this session</p>
        </div>
      </div>

      {activePages.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pages Active Now</h4>
          <div className="space-y-2">
            {activePages.map(ap => {
              const pct = Math.round((ap.count / activeNow) * 100) || 0;
              return (
                <div key={ap.path} className="flex items-center space-x-3">
                  <p className="text-sm font-mono text-gray-700 flex-1 truncate">{ap.path}</p>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="w-20 bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-green-400 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-4 text-right">{ap.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <h3 className="text-sm font-semibold text-gray-700">Live Activity Stream</h3>
          </div>
          <span className="text-xs text-gray-400">{events.length} events</span>
        </div>

        <div ref={containerRef} className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-8 h-8 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center mb-2">
                <span className="w-2 h-2 bg-gray-300 rounded-full" />
              </div>
              <p className="text-sm text-gray-400">Waiting for visitors...</p>
              <p className="text-xs text-gray-300 mt-0.5">Events will appear here in real time</p>
            </div>
          ) : (
            events.map(ev => (
              <div
                key={ev.id}
                className="flex items-start justify-between py-2 px-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-start space-x-2.5 min-w-0">
                  <div className={`mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full ${ev.type === 'new_session' ? 'bg-green-100 text-green-600' : 'bg-orange-50 text-orange-500'}`}>
                    {ev.type === 'new_session'
                      ? <span className="w-2 h-2 rounded-full bg-green-500" />
                      : <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                    }
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center space-x-1.5 flex-wrap gap-y-0.5">
                      <p className="text-sm font-mono text-gray-800 truncate max-w-[160px]">{ev.path ?? '/'}</p>
                      {ev.type === 'new_session' && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                          {ev.is_returning ? 'Returning' : 'New'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 mt-0.5 flex-wrap gap-y-0.5">
                      {ev.browser && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${getBrowserColor(ev.browser)}`}>
                          {ev.browser}{ev.browser_version ? ` ${ev.browser_version}` : ''}
                        </span>
                      )}
                      {ev.device && (
                        <span className="flex items-center space-x-1 text-[10px] text-gray-400">
                          <DeviceIcon type={ev.device} />
                          <span className="capitalize">{ev.device}</span>
                        </span>
                      )}
                      {ev.os && <span className="text-[10px] text-gray-400">{ev.os}</span>}
                      {(ev.city || ev.country) && (
                        <span className="text-[10px] text-gray-400 flex items-center space-x-0.5">
                          <GlobeAltIcon className="w-3 h-3" />
                          <span>{[ev.city, ev.country].filter(Boolean).join(', ')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-2 text-right">
                  <p className="text-[10px] text-gray-400 font-mono">{format(parseISO(ev.ts), 'HH:mm:ss')}</p>
                  <p className="text-[10px] text-gray-300">{formatDistanceToNowStrict(parseISO(ev.ts), { addSuffix: true })}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
